// Best-effort repair of LLM-authored Mermaid flowcharts.
//
// The lesson-text prompt asks for diagrams, and the model routinely writes
// labels Mermaid cannot parse - most often an unquoted `(` inside a `[...]`
// label (`C[F = ma (Second Law)]`), which Mermaid reads as a new node shape.
// Quoting the label (`C["F = ma (Second Law)"]`) is always valid, so this
// rewrites only labels that contain a reserved character and are not already
// quoted. It is deliberately conservative: flowcharts only, line by line, and
// MermaidBlock still falls back to the raw source if the result won't parse.

const FLOWCHART_HEADER = /^\s*(graph|flowchart)\b/;
const RESERVED = /[()[\]{}<>|"]/;
// Lines that are not node/edge statements - leave them untouched.
const SKIP_LINE =
  /^\s*(%%|classDef\b|class\b|style\b|linkStyle\b|click\b|subgraph\b|end\b|direction\b)/;
// After a node label closes, only an edge, `&`, `;`, a class suffix or the end
// of the line may follow - this is how a label's real closer is told apart
// from a bracket that is merely part of the label text.
const AFTER_NODE = /^\s*($|;|&|:::|--|==|-\.|~~~)/;

// Longest openers first so `[[` is not read as `[`.
const SHAPES: ReadonlyArray<readonly [string, string]> = [
  ['[[', ']]'],
  ['[(', ')]'],
  ['([', '])'],
  ['((', '))'],
  ['{{', '}}'],
  ['[', ']'],
  ['(', ')'],
  ['{', '}'],
];

const ID_CHAR = /[A-Za-z0-9_]/;

function quote(label: string): string {
  return `"${label.replace(/"/g, '#quot;')}"`;
}

function needsQuoting(label: string): boolean {
  const trimmed = label.trim();
  return !trimmed.startsWith('"') && RESERVED.test(trimmed);
}

// Arrows a `|label|` can follow. Models often leave a space before the pipe
// (`--> |e(t) = SP - PV(t)|`), which the label repair has to allow for.
const LABELLED_EDGE = /(-->|---|==>|===|-\.->|-\.-)\s*\|([^|]*)\|/g;

/** `A -- text --> B` and `A --> |text| B` → `A -->|"text"| B` when needed. */
function repairEdgeLabels(line: string): string {
  return line
    .replace(/(\s)--\s+([^-\s][^|]*?)\s+-->(\s)/g, (match, pre, text, post) =>
      needsQuoting(text) ? `${pre}-->|${quote(text.trim())}|${post}` : match
    )
    .replace(LABELLED_EDGE, (match, arrow, text) =>
      needsQuoting(text) ? `${arrow}|${quote(text.trim())}|` : match
    );
}

/** Index just past a quoted (`"…"`) or piped (`|…|`) span starting at `i`. */
function skipSpan(line: string, i: number): number {
  const end = line.indexOf(line[i], i + 1);
  return end === -1 ? line.length : end + 1;
}

/** The shape whose opener starts at `i`, if any. */
function shapeAt(line: string, i: number): readonly [string, string] | null {
  return SHAPES.find(([open]) => line.startsWith(open, i)) ?? null;
}

/** Index of the closer that really ends a label opened before `from`. */
function findCloser(line: string, from: number, close: string): number {
  let at = line.indexOf(close, from);
  while (at !== -1) {
    if (AFTER_NODE.test(line.slice(at + close.length))) return at;
    at = line.indexOf(close, at + 1);
  }
  return -1;
}

/**
 * Quote the node label whose opener starts at `i`. Returns the rewritten line
 * and the index to resume scanning from.
 */
function repairNodeAt(line: string, i: number): [string, number] {
  const shape = shapeAt(line, i);
  if (!shape) return [line, i];
  const [open, close] = shape;
  const start = i + open.length;
  const end = findCloser(line, start, close);
  if (end === -1) return [line, start];
  const label = line.slice(start, end);
  if (!needsQuoting(label)) return [line, end + close.length];
  const fixed = quote(label.trim());
  const next = line.slice(0, start) + fixed + line.slice(end);
  return [next, start + fixed.length + close.length];
}

function repairNodeLabels(line: string): string {
  let out = line;
  let i = 0;
  while (i < out.length) {
    const ch = out[i];
    if (ch === '"' || ch === '|') {
      i = skipSpan(out, i);
    } else if (ID_CHAR.test(ch) && (i === 0 || !ID_CHAR.test(out[i - 1]))) {
      let j = i;
      while (j < out.length && ID_CHAR.test(out[j])) j += 1;
      [out, i] = repairNodeAt(out, j);
    } else {
      i += 1;
    }
  }
  return out;
}

/**
 * Quote flowchart node/edge labels that contain characters Mermaid reserves.
 * Returns the source unchanged for non-flowchart diagrams.
 */
export function repairMermaid(source: string): string {
  if (!FLOWCHART_HEADER.test(source)) return source;
  return source
    .split('\n')
    .map(line =>
      SKIP_LINE.test(line) ? line : repairNodeLabels(repairEdgeLabels(line))
    )
    .join('\n');
}
